import { _, _any } from "i18n";
import { Keys } from "i18n/locales/types";
import { ColumnEnum, PlayerPosition } from "../types";

export const DEFAULT_POSITION = "ALL";
export const DEFAULT_RETURN_VALUE: string = "";
export const DEFAULT_EMPTY_NUMBER_VALUE: string = "N/A";
export const DEFAULT_PAGE_SIZE = 18;

export const IMAGES = import.meta.glob("assets/images/flags/*.png", { query: "?url", import: "default", eager: true }) as Record<string, string>;
export const BIRTHDAY_MIN_YEAR = 1997;
export const BIRTHDAY_MAX_YEAR = 2019;

export const EYEBALL_RATING_MIN = 0;
export const EYEBALL_RATING_MAX = 10;

export const TILES_COLORS = {
	green1: { background: "#6EE7B7", color: "#065F46" },
	green2: { background: "#CEF6ED", color: "#057F62" },
	grey: { background: "#DEDEE3", color: "#000938" },
	red4: { background: "#F6CEDB", color: "#7F052E" },
	red5: { background: "#E05282", color: "#7F052E" },
};

export const PORTAL_TILE_COLORS = {
	green1: { background: "#93E567", color: "#457F26" },
	green2: { background: "#D7F5C7", color: "#457F26" },
	grey: { background: "#DDDDDDB3", color: "#000937" },
	red4: { background: "#FFCCCF", color: "#7F2626" },
	red5: { background: "#FF7272", color: "#661E1E" },
};

export const STATS_FILTER_ENTITY = {
	rating: { gte: 0, lt: null },
	minutesPlayed: { gte: 90, lt: 900 },
	gamesCount: { gte: 1, lt: null },
	goalsScored: { gte: 0, lt: null },
	goalsOwn: { gte: 0, lt: null },
	assists: { gte: 0, lt: null },
	totalGoals: { gte: 0, lt: null },
	totalAssists: { gte: 0, lt: null },
	shots: { gte: 0, lt: null },
	shotsOnTarget: { gte: 0, lt: null },
	shotsInsidePa: { gte: 0, lt: null },
	shotsOutsidePa: { gte: 0, lt: null },
	takeons: { gte: 0, lt: null },
	tackles: { gte: 0, lt: null },
	aerialDuels: { gte: 0, lt: null },
	groundDuels: { gte: 0, lt: null },
	recoveries: { gte: 0, lt: null },
	passes: { gte: 0, lt: null },
	passesAccuracy: { gte: 0, lt: null },
	keyPasses: { gte: 0, lt: null },
	passesForward: { gte: 0, lt: null },
	crosses: { gte: 0, lt: null },
	controlUnderPressure: { gte: 0, lt: null },
	goalsConceded: { gte: 0, lt: null },
	catches: { gte: 0, lt: null },
	punches: { gte: 0, lt: null },
	goalsKicks: { gte: 0, lt: null },
	aerialClearances: { gte: 0, lt: null },
};

export const STATS_OPTIONS: Record<string, keyof Keys> = {
	General: "game.stats.menu.general",
	Forward: "player.forward",
	Midfielder: "player.midfielder",
	Defender: "player.defender",
	Goalkeeper: "enum.position.GK",
	Custom: "label.custom",
};

export const DEFAULT_GRID_COLUMNS: Record<ColumnEnum, string> = {
	name: "Name",
	gender: "Gender",
	clubName: "Club Name",
	teamName: "Team Name",
	teamAgeGroup: "Team Age Group",
	strongFoot: "Strong Foot",
	position: "Position",
	birthday: "Birth Day",
	height: "Height",
	weight: "Weight",
	nationality: "Nat",
	rating: "Rating",
	minutesPlayed: "Minutes Played",
	gamesCount: "Games",
	goalsScored: "Goals Scored",
	goalsOwn: "Goals Own",
	assists: "Assists",
	totalGoals: "totalGoals",
	totalAssists: "totalAssists",
	shots: "Shots",
	shotsOnTarget: "Shots On Target",
	shotsInsidePa: "Shots Inside",
	shotsOutsidePa: "Shots Outside",
	takeons: "Take ons",
	tackles: "Tackles",
	aerialDuels: "Aerial Duels",
	groundDuels: "Ground Duels",
	recoveries: "Recoveries",
	passes: "Passes",
	passesAccuracy: "Passes Accuracy",
	keyPasses: "Key Passes",
	passesForward: "Passes Forward",
	crosses: "Crosses",
	controlUnderPressure: "Control under pressure",
	goalsConceded: "Goals Conceded",
	catches: "Catches",
	punches: "Punches",
	goalsKicks: "Goals Kicks",
	aerialClearances: "Aerial Clearances",
};

const { gender: _g, ...recruiterBase } = DEFAULT_GRID_COLUMNS;
export const RECRUITER_GRID_COLUMNS: Partial<Record<ColumnEnum, string>> = recruiterBase;

// backwards-compat alias
export const GRID_COLUMNS = DEFAULT_GRID_COLUMNS;

export const GRID_COLUMNS_KEYS: ColumnEnum[] = Object.values(ColumnEnum);

// Mapping from column accessor to API sort key
export const COLUMN_TO_SORT_KEY: Record<ColumnEnum, string> = {
	name: "playerName",
	clubName: "clubName",
	teamName: "teamName",
	teamAgeGroup: "teamAgeGroup",
	strongFoot: "strongFoot",
	position: "position",
	birthday: "birthday",
	height: "height",
	weight: "weight",
	nationality: "nationality",
	rating: "rating",
	minutesPlayed: "minutesPlayed",
	gamesCount: "gamesCount",
	goalsScored: "goalsScored",
	goalsOwn: "goalsOwn",
	assists: "assists",
	totalGoals: "totalGoals",
	totalAssists: "totalAssists",
	shots: "shots",
	shotsOnTarget: "shotsOnTarget",
	shotsInsidePa: "shotsInsidePa",
	shotsOutsidePa: "shotsOutsidePa",
	takeons: "takeons",
	tackles: "tackles",
	aerialDuels: "aerialDuels",
	groundDuels: "groundDuels",
	recoveries: "recoveries",
	passes: "passes",
	passesAccuracy: "passesAccuracy",
	keyPasses: "keyPasses",
	passesForward: "passesForward",
	crosses: "crosses",
	controlUnderPressure: "controlUnderPressure",
	goalsConceded: "goalsConceded",
	catches: "catches",
	punches: "punches",
	goalsKicks: "goalsKicks",
	aerialClearances: "aerialClearances",
	gender: "gender",
};

export const TEAM_LEVELS = ["-", "1", "2", "3", "4"].map((value) => ({ value: value, label: value }));
export const TEAM_AGE_GROUP = [
	"-",
	"U6",
	"U7",
	"U8",
	"U9",
	"U10",
	"U11",
	"U12",
	"U13",
	"U14",
	"U15",
	"U16",
	"U17",
	"U18",
	"U19",
	"U20",
	"U21",
	"U23",
	"SENIOR",
	"N2",
	"N3",
].map((value) => ({
	value: value,
	label: value,
}));

export const PLAYER_POSITIONS = [
	{ value: PlayerPosition.ALL, label: "-" },
	{ value: PlayerPosition.G, label: "enum.position.G" },
	{ value: PlayerPosition.DD, label: "enum.position.DD" },
	{ value: PlayerPosition.DC, label: "enum.position.DC" },
	{ value: PlayerPosition.DG, label: "enum.position.DG" },
	{ value: PlayerPosition.MDC, label: "enum.position.MDC" },
	{ value: PlayerPosition.MC, label: "enum.position.MC" },
	{ value: PlayerPosition.MOC, label: "enum.position.MOC" },
	{ value: PlayerPosition.MG, label: "enum.position.MG" },
	{ value: PlayerPosition.MD, label: "enum.position.MD" },
	{ value: PlayerPosition.ATT, label: "enum.position.ATT" },
	{ value: PlayerPosition.AG, label: "enum.position.AG" },
	{ value: PlayerPosition.AD, label: "enum.position.AD" },
] as const;

export const PLAYER_POSITION_ORDER = [
	PlayerPosition.G,
	PlayerPosition.DG,
	PlayerPosition.DC,
	PlayerPosition.DD,
	PlayerPosition.MDC,
	PlayerPosition.MG,
	PlayerPosition.MC,
	PlayerPosition.MOC,
	PlayerPosition.MD,
	PlayerPosition.AG,
	PlayerPosition.ATT,
	PlayerPosition.AD,
] as const;

export const STATS_FILTERS = [
	"rating",
	"goalsScored",
	"goalsOwn",
	"assists",
	"totalGoals",
	"totalAssists",
	"passes",
	"passesAccuracy",
	"keyPasses",
	"passesForward",
	"crosses",
	"controlUnderPressure",
	"tackles",
	"aerialDuels",
	"groundDuels",
	"recoveries",
	"goalsConceded",
	"catches",
	"punches",
	"goalsKicks",
	"aerialClearances",
	"shotsOnTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"minutesPlayed",
	"shots",
	"gamesCount",
	"takeons",
];

export const NON_SCALE_STAT_KEYS: string[] = ["minutesPlayed", "gamesCount", "takeons"];

/** Stat filter keys that use 0–100% sliders; stored/API scale 0–1. All other stats use raw value ranges (e.g. minutes 90–900, games 1–100). */
export const PERCENTAGE_STAT_KEYS: string[] = [
	"rating",
	"assists",
	"passes",
	"passesAccuracy",
	"keyPasses",
	"passesForward",
	"crosses",
	"controlUnderPressure",
	"tackles",
	"aerialDuels",
	"groundDuels",
	"recoveries",
	"goalsConceded",
	"catches",
	"punches",
	"goalsKicks",
	"aerialClearances",
	"shotsOnTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"shots",
	"takeons",
];

export const LEFT_FILTERS = [
	"birthday",
	"clubName",
	"countryCodes",
	"height",
	"nationality",
	"playerName",
	"position",
	"strongFoot",
	"playedInNationalTeam",
	"teamAgeGroup",
	"teamLevel",
	"weight",
];

export const VISIBLE_COLUMNS = [
	{
		id: "1",
		key: "General",
		values: [
			"name",
			"clubName",
			"teamName",
			"strongFoot",
			"position",
			"birthday",
			"nationality",
			"gender",
			"rating",
			"minutesPlayed",
			"gamesCount",
			"goalsScored",
			"assists",
		],
	},
	{
		id: "2",
		key: "Forward",
		values: [
			"name",
			"clubName",
			"teamName",
			"nationality",
			"gender",
			"teamAgeGroup",
			"rating",
			"minutesPlayed",
			"takeOns",
			"crosses",
			"shots",
			"assists",
			"keyPasses",
			"goalsScored",
			"shotsOnTarget",
			"shotsInsidePa",
			"controlUnderPressure",
		],
	},
	{
		id: "3",
		key: "Midfielder",
		values: [
			"name",
			"clubName",
			"teamName",
			"nationality",
			"gender",
			"teamAgeGroup",
			"rating",
			"minutesPlayed",
			"keyPasses",
			"passesAccuracy",
			"controlUnderPressure",
			"shots",
			"passesForward",
			"assists",
			"goalsScored",
			"takeOns",
		],
	},
	{
		id: "4",
		key: "Defender",
		values: [
			"name",
			"clubName",
			"teamName",
			"nationality",
			"gender",
			"teamAgeGroup",
			"rating",
			"minutesPlayed",
			"aerialDuels",
			"groundDuels",
			"tackles",
			"passesForward",
			"crosses",
			"takeOns",
			"recoveries",
			"assists",
		],
	},
	{
		id: "5",
		key: "Goalkeeper",
		values: [
			"name",
			"clubName",
			"teamName",
			"nationality",
			"gender",
			"teamAgeGroup",
			"rating",
			"minutesPlayed",
			"goalsConceded",
			"aerialClearances",
			"passesAccuracy",
		],
	},
];

/**
 * Season calculation functions
 *
 * This module handles season year calculations based on match dates and country codes
 */

export const AUGUST_TO_JUNE = ["DE", "PL", "CH"];

export const AUGUST_TO_MAY = [
	"AL",
	"AM",
	"AT",
	"AZ",
	"BE",
	"BA",
	"BG",
	"HR",
	"CZ",
	"FR",
	"HU",
	"XK",
	"LI",
	"MD",
	"ME",
	"NL",
	"MK",
	"RO",
	"RS",
	"SK",
	"SI",
	"TR",
	"UA",
	"GB",
	"IL",
	"DZ",
	"AO",
	"BW",
	"BI",
	"SZ",
	"ZA",
	"TZ",
	"ZM",
	"BZ",
	"PR",
	"SA",
	"AE",
	"QA",
	"XE",
	"XW",
	"XS",
	"XN",
	"XI",
];

export const JULY_TO_MAY = ["RU", "CR", "SV", "GT", "HN", "NI", "PA", "MX"];

export const NOVEMBER_TO_JUNE = ["BJ", "CF", "TD", "GM", "GW", "MR", "NE", "SN", "SO", "SS"];

export const OTOBER_TO_JUNE = ["BF", "CV", "ET", "GA", "GN", "LR", "LY", "MG", "ML", "ST", "SL", "TG"];

export const OCTOBER_TO_MAY = ["KM", "CD", "CG", "DJ", "GQ", "ER", "MU", "SC", "SD"];

export const SEPTEMBER_TO_JUNE = ["ES", "CI", "EG", "GH", "KE", "MA", "NG", "RW", "TN", "JM", "US"];

export const SEPTEMBER_TO_MAY = [
	"AD",
	"CY",
	"GI",
	"GR",
	"IT",
	"LU",
	"MT",
	"MC",
	"PT",
	"SM",
	"CM",
	"LS",
	"NA",
	"UG",
	"TT",
	"BB",
	"BS",
	"GD",
	"LC",
	"VC",
	"AG",
	"KN",
	"DM",
];

export const SEASONS = {
	AUGUST_TO_JUNE: {
		start: 8,
		end: 6,
	},
	AUGUST_TO_MAY: {
		start: 8,
		end: 5,
	},
	JULY_TO_MAY: {
		start: 7,
		end: 5,
	},
	NOVEMBER_TO_JUNE: {
		start: 11,
		end: 6,
	},
	OTOBER_TO_JUNE: {
		start: 10,
		end: 6,
	},
	OCTOBER_TO_MAY: {
		start: 10,
		end: 5,
	},
	SEPTEMBER_TO_JUNE: {
		start: 9,
		end: 6,
	},
	SEPTEMBER_TO_MAY: {
		start: 9,
		end: 5,
	},
};
